import { useEffect, useState } from 'react';
import {
  generatePopularReportsAction,
  getPopularReportsAction,
  schedulePopularReportsAction,
} from 'contexts/reporting/actions';
import { IPopularReport, IPopularReportsDataCtx } from 'contexts/reporting/types';
import Notification from 'components/notification';
import { filters as getReportingFilters } from 'pages/Reporting/components/ReportingFiltersConfig';
import { ACCOUNT, COURSES, TEAM_ACTIVITY, USER_ACTIVITY } from 'constants/reporting';

type PopularReportDetailsGroup = {
  title: string;
  items: string[];
};
type FilterItemInfo = { name: string; label: string };
type FiltersGroupMap = { order: string[]; groups: Record<string, FilterItemInfo[]> };
type PopularReportFiltersMap = Record<string, FiltersGroupMap>;

let popularReportFiltersMapCache: PopularReportFiltersMap | null = null;
const TYPE_KEYS = [USER_ACTIVITY, TEAM_ACTIVITY, COURSES, ACCOUNT];

// Build a map once from ReportingFiltersConfig to speed up lookups for all popular reports
const getPopularReportFiltersMap = (): PopularReportFiltersMap => {
  if (popularReportFiltersMapCache) return popularReportFiltersMapCache;

  const base: PopularReportFiltersMap = TYPE_KEYS.reduce((acc, t) => {
    acc[t] = { order: [], groups: {} };
    return acc;
  }, {} as PopularReportFiltersMap);

  const allGroups = getReportingFilters();

  allGroups.forEach((group) => {
    TYPE_KEYS.forEach((t) => {
      if (!group.showTo?.includes(t)) return;

      if (!base[t].order.includes(group.name)) base[t].order.push(group.name);
      if (!base[t].groups[group.name]) base[t].groups[group.name] = [];

      const items: FilterItemInfo[] = group.filters
        .filter((f) => f.showTo?.includes(t))
        .map((f) => ({ name: f.name, label: f.label }));

      base[t].groups[group.name].push(...items);
    });
  });

  // Cache and shallow-freeze to avoid accidental mutation
  TYPE_KEYS.forEach((t) => {
    base[t].order = Object.freeze([...base[t].order]) as unknown as string[];
    Object.keys(base[t].groups).forEach((g) => {
      base[t].groups[g] = Object.freeze([...base[t].groups[g]]) as unknown as FilterItemInfo[];
    });
  });

  popularReportFiltersMapCache = base;
  return base;
};

const buildPopularReportDetails = (
  type: string,
  checkboxes: string[]
): PopularReportDetailsGroup[] => {
  const map = getPopularReportFiltersMap();
  const perType = map[type] || { order: [], groups: {} };

  const selected = new Set(checkboxes || []);

  const rawGroups: PopularReportDetailsGroup[] = perType.order.reduce(
    (acc: PopularReportDetailsGroup[], groupName, index) => {
      const items = (perType.groups[groupName] || [])
        .filter((item) => selected.has(item.name))
        .map((item) => item.label);

      if (items.length) acc.push({ title: groupName, items });
      return acc;
    },
    []
  );

  // CheckBoxesGroup: groups that have identical checkboxes label list
  type CheckBoxesGroup = { titles: string[]; items: string[]; firstIndex: number };
  const CheckBoxesGroupList = new Map<string, CheckBoxesGroup>();

  rawGroups.forEach((grp, idx) => {
    // Create the same “key” for groups that contain the same items (despite items order)
    const signature = [...grp.items].sort().join('\u0001');
    const existing = CheckBoxesGroupList.get(signature);
    if (!existing) {
      CheckBoxesGroupList.set(signature, {
        titles: [grp.title],
        items: grp.items,
        firstIndex: idx,
      });
    } else {
      existing.titles.push(grp.title);
      existing.firstIndex = Math.min(existing.firstIndex, idx);
    }
  });

  return [...CheckBoxesGroupList.values()]
    .sort((a, b) => a.firstIndex - b.firstIndex)
    .map((b) => ({ title: b.titles.join(', '), items: b.items }));
};

type GeneratePopularReportParams = {
  popular_report_id: string;
  from_date: string;
  to_date: string;
  uaa_ids?: string[];
  team_ids?: string[];
};

const usePopularReportContent = (): IPopularReportsDataCtx => {
  const [fetchingPopularReports, setFetchingPopularReports] = useState(false);
  const [updatingPopularReport, setUpdatingPopularReport] = useState(false);
  const [popularReports, setPopularReports] = useState<IPopularReport[]>([]);

  const getPopularReports = async () => {
    setFetchingPopularReports(true);
    try {
      const popularReports = await getPopularReportsAction();
      setPopularReports(popularReports);
    } catch (error) {
      Notification({ text: 'There has been a problem fetching popular reports', type: 'error' });
    } finally {
      setFetchingPopularReports(false);
    }
  };

  const generatePopularReport = async (params: GeneratePopularReportParams): Promise<boolean> => {
    setUpdatingPopularReport(true);
    try {
      await generatePopularReportsAction(params);
      Notification({
        text: 'Report generated successfully! Find it in Report History',
        type: 'success',
      });
      return true;
    } catch (error) {
      Notification({ text: 'There has been a problem generating your report', type: 'error' });
      return false;
    } finally {
      setUpdatingPopularReport(false);
    }
  };

  const schedulePopularReport = async (frequency: string[], reportId: string) => {
    setUpdatingPopularReport(true);
    try {
      await schedulePopularReportsAction(frequency, reportId);
      await getPopularReports();
      Notification({
        text: 'Report scheduled! Edit preferences in “Manage my scheduled reports”',
        type: 'success',
        allowWidthGrow: true,
      });
    } catch (error) {
      Notification({ text: 'There has been a problem scheduling your report', type: 'error' });
    } finally {
      setUpdatingPopularReport(false);
    }
  };

  useEffect(() => {
    getPopularReports();
  }, []);

  return {
    fetchingPopularReports,
    updatingPopularReport,
    popularReports,
    generatePopularReport,
    schedulePopularReport,
    buildPopularReportDetails,
  };
};

export default usePopularReportContent;
